"use client"

import { useState } from "react"
import Image from "next/image"
import dynamic from "next/dynamic"
import Link from "next/link"
import { Heading } from "@medusajs/ui"

const RouletteWheel = dynamic(
  () => import("react-custom-roulette").then((mod) => mod.Wheel),
  {
    ssr: false,
    loading: () => (
      <div className="mx-auto flex h-[80vw] w-[80vw] max-h-[445px] max-w-[445px] items-center justify-center rounded-full border border-[#ebe7e1] bg-white text-sm uppercase tracking-[0.16em] text-[#7d7167]">
        Cargando ruleta
      </div>
    ),
  }
)

const INSTAGRAM_URL = "https://instagram.com/lealtiofficial"
const TRY_AGAIN_PRIZE = "¡SEGUI PARTICIPANDO!"

const PRIZES = [
  "10% OFF EN TODO LEALTI",
  "MEDIALUNAS DE MEDIALUNAS CALENTITAS",
  TRY_AGAIN_PRIZE,
  "20% OFF EN TRACTO",
  "2X1 EN ACCESORIOS DE SACRAMENTO",
]

const PRIZE_COLORS = [
  { backgroundColor: "#1d1b1a", textColor: "#f8f6f3" },
  { backgroundColor: "#8c6b4f", textColor: "#f8f5f0" },
  { backgroundColor: "#c9b49c", textColor: "#272220" },
  { backgroundColor: "#5b6b7a", textColor: "#f8f6f2" },
  { backgroundColor: "#7d5a64", textColor: "#f9f5f1" },
]

const WHEEL_DATA = PRIZES.map((prize, index) => ({
  option: prize,
  style: PRIZE_COLORS[index],
}))

export default function RouletteSection() {
  const [prizeNumber, setPrizeNumber] = useState(0)
  const [mustSpin, setMustSpin] = useState(false)
  const [winningPrize, setWinningPrize] = useState<string | null>(null)
  const isTryAgain = winningPrize === TRY_AGAIN_PRIZE

  const handleSpinClick = () => {
    if (mustSpin) {
      return
    }

    const randomPrizeNumber = Math.floor(Math.random() * PRIZES.length)
    setPrizeNumber(randomPrizeNumber)
    setWinningPrize(null)
    setMustSpin(true)
  }

  const handleStopSpinning = () => {
    setMustSpin(false)
    setWinningPrize(PRIZES[prizeNumber])
  }

  return (
    <section className="overflow-x-hidden bg-white py-6 md:py-12">
      <div className="content-container">
        <div className="mx-auto max-w-5xl bg-white p-0 sm:p-0 md:p-0">
          <div className="flex flex-col gap-8 lg:grid lg:grid-cols-[0.95fr_1.05fr] lg:items-center lg:gap-10">
            <div className="order-2 flex flex-col justify-center gap-5 lg:order-1 lg:gap-6">
              <p className="inline-flex w-fit items-center rounded-full border border-black/20 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-black/70">
                Solo en QR • Promo
              </p>

              <Heading
                level="h1"
                className="font-lealti text-3xl leading-tight text-[#1d1a18] sm:text-4xl md:text-[3rem]"
              >
                Girá la ruleta y descubrí tu premio
              </Heading>

              <p className="max-w-xl text-lg text-[#2f2a26] md:text-xl">
                Válido solo para quienes logren tocar al hombre QR!
              </p>

              <p className="max-w-xl text-base leading-relaxed text-[#5b544d]">
                Cada giro entrega un resultado aleatorio. Si ganás, guardá la
                captura y reclamá el premio por Instagram.
              </p>

              <button
                type="button"
                onClick={handleSpinClick}
                disabled={mustSpin}
                className="w-full rounded-full border border-[#24201d]/20 bg-[#24201d] px-7 py-3 text-base uppercase tracking-[0.14em] text-white transition-all duration-300 hover:bg-black disabled:cursor-not-allowed disabled:bg-[#6d655d] sm:w-fit"
              >
                {mustSpin ? "Girando..." : "Girar ruleta"}
              </button>

              <div className="rounded-2xl border border-[#ebe7e1] bg-white p-4 sm:p-5">
                <p className="text-sm uppercase tracking-[0.15em] text-[#6d6257]">
                  Premios disponibles
                </p>
                <ul className="mt-3 space-y-2 text-base text-[#332d28]">
                  {PRIZES.map((prize) => (
                    <li key={prize}>• {prize}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="order-1 flex flex-col items-center justify-center gap-5 lg:order-2 lg:gap-6">
              <div className="relative mx-auto flex w-full justify-center">
                <div className="relative">
                  <RouletteWheel
                    mustStartSpinning={mustSpin}
                    prizeNumber={prizeNumber}
                    data={WHEEL_DATA}
                    spinDuration={0.55}
                    onStopSpinning={handleStopSpinning}
                    outerBorderColor="#201b18"
                    outerBorderWidth={3}
                    radiusLineColor="#f1e9de"
                    radiusLineWidth={2}
                    innerRadius={8}
                    textDistance={52}
                    fontSize={11}
                    fontFamily="sans-serif"
                    pointerProps={{
                      style: {
                        width: "38px",
                      },
                    }}
                  />
                </div>
              </div>

              {winningPrize ? (
                <div className="w-full rounded-2xl border border-[#ebe7e1] bg-white p-4 text-center animate-fade-in-top sm:p-5">
                  <p
                    className={`text-xs uppercase tracking-[0.18em] ${
                      isTryAgain ? "text-[#7a6751]" : "text-[#4e6352]"
                    }`}
                  >
                    {isTryAgain ? "Seguí intentando" : "Resultado"}
                  </p>
                  <p
                    className={`mt-2 text-xl md:text-2xl ${
                      isTryAgain ? "text-[#544230]" : "text-[#27372a]"
                    }`}
                  >
                    {winningPrize}
                  </p>
                  <p className="mt-3 text-base leading-relaxed text-[#2e2925]">
                    ¡Seguinos en Instagram (
                    <Link
                      href={INSTAGRAM_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="font-semibold underline underline-offset-4"
                    >
                      instagram.com/lealtiofficial
                    </Link>
                    )! Y enviamos una captura de pantalla para reclamar tu
                    premio. VALIDO UNICAMENTE PARA QUIENES LOGRARON TOCAR AL
                    HOMBRE QR
                  </p>
                  <Link
                    href={INSTAGRAM_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="mx-auto mt-4 inline-flex items-center gap-2 rounded-full border border-[#1f1b18]/20 bg-white px-5 py-2 text-sm font-semibold text-[#1f1b18] transition hover:bg-[#1f1b18] hover:text-white"
                  >
                    <Image
                      src="/instagram-logo.png"
                      alt="Instagram"
                      width={16}
                      height={16}
                    />
                    Ir a Instagram
                  </Link>
                </div>
              ) : (
                <div className="w-full rounded-2xl border border-[#ebe7e1] bg-white p-4 text-center text-[#474038] sm:p-5">
                  Presioná &quot;Girar ruleta&quot; para jugar.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
